/**
 * "Preview" badges on paid controls (#1120).
 *
 * Paid features are early-access features during the public preview. Each control that
 * belongs to one carries `data-paid-feature="<slug>"`; when that slug is paid
 * (per `docs/features/` tiers, generated into `virtual:paid-features` at build
 * time) it also gets `data-preview`, which the stylesheet draws as a small
 * corner badge, and the preview note in its tooltip.
 *
 * Controls in `index.html` are marked at build time by the same plugin, so
 * they're badged at first paint. This module covers the controls built in
 * TypeScript.
 */
import { PAID_FEATURES, PREVIEW_NOTE } from "virtual:paid-features";

const PAID = new Set(PAID_FEATURES);

/** Whether `slug` is a paid feature. */
export function isPaidFeature(slug: string): boolean {
  return PAID.has(slug);
}

/**
 * Tag `el` as a control of feature `slug`, and badge it when the feature is
 * paid. `active: false` drops the badge, for a control that only routes to the
 * paid feature while its feature flag is on (Print, Export → PDF / SVG / DXF).
 *
 * Safe to call repeatedly: the control's own tooltip is kept in
 * `data-preview-base-title`, so the note is never appended twice.
 */
export function markPaidFeature(
  el: HTMLElement,
  slug: string,
  active = true,
): void {
  el.dataset.paidFeature = slug;
  if (el.dataset.previewBaseTitle === undefined) {
    el.dataset.previewBaseTitle = el.getAttribute("title") ?? "";
  }
  const base = el.dataset.previewBaseTitle;
  const on = active && PAID.has(slug);
  el.toggleAttribute("data-preview", on);
  if (on) el.title = base ? `${base}\n\n${PREVIEW_NOTE}` : PREVIEW_NOTE;
  else if (base) el.title = base;
  else el.removeAttribute("title");
}

/** Badge a whole properties-panel section (built by `panelSection`) by its
 *  heading, for a section that belongs entirely to paid feature `slug`. */
export function markPaidSection(section: HTMLElement, slug: string): void {
  const heading = section.querySelector<HTMLElement>(
    ":scope > .panel-section-heading",
  );
  if (heading) markPaidFeature(heading, slug);
}

/** `title` without the preview note, for code that rebuilds a paid control's
 *  tooltip from its initial one (the note comes from index.html). */
export function withoutPreviewNote(title: string): string {
  const suffix = `\n\n${PREVIEW_NOTE}`;
  if (title === PREVIEW_NOTE) return "";
  return title.endsWith(suffix) ? title.slice(0, -suffix.length) : title;
}

/** `title` plus the preview note when `el` is badged, so a rebuilt tooltip
 *  keeps the note last, after anything the control appends to its name. */
export function withPreviewNote(el: HTMLElement, title: string): string {
  if (!el.hasAttribute("data-preview")) return title;
  return title ? `${title}\n\n${PREVIEW_NOTE}` : PREVIEW_NOTE;
}
